import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../auth/AuthContext";
import { qrMatrix } from "@equipped/core/qr/qrMatrix";
import { qrMatrixToSvg } from "@equipped/core/qr/qrSvg";

export function SecurityPage() {
  const { client, token } = useAuth();

  const [step, setStep] = useState<"idle" | "setup" | "recovery">("idle");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [setupData, setSetupData] = useState<{ secret: string; uri: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleBeginSetup = async () => {
    if (!client || !token) return;
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await client.totpBegin(token);
      setSetupData({ secret: res.secret_base32, uri: res.otpauth_uri });
      setStep("setup");
    } catch (err: any) {
      setError(err?.message || "Failed to initiate 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async () => {
    if (!client || !token || !verifyCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.totpConfirm(token, verifyCode.trim());
      setRecoveryCodes(res.recovery_codes || []);
      setStep("recovery");
    } catch (err: any) {
      setError(err?.message || "Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinishSetup = () => {
    setStep("idle");
    setSetupData(null);
    setVerifyCode("");
    setRecoveryCodes([]);
    setSuccessMsg("Two-factor authentication successfully enabled.");
  };

  const handleDisable2FA = async () => {
    if (!client || !token) return;
    setDisableLoading(true);
    setDisableError(null);
    try {
      await client.disable2fa(token, { code: disableCode.trim() || undefined });
      setDisableDialogOpen(false);
      setDisableCode("");
      setStep("idle");
      setSetupData(null);
      setSuccessMsg("Two-factor authentication disabled.");
    } catch (err: any) {
      setDisableError(err?.message || "Failed to disable 2FA. Check your code and try again.");
    } finally {
      setDisableLoading(false);
    }
  };

  let qrSvg = "";
  if (setupData?.uri) {
    try {
      qrSvg = qrMatrixToSvg(qrMatrix(setupData.uri), { moduleSize: 5 });
    } catch {
      qrSvg = "";
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        My Security
      </Typography>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Two-Factor Authentication (2FA)"
          subheader="Add an extra layer of security to your account using TOTP authenticator apps."
        />
        <Divider />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {step === "idle" && (
            <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
              <Typography variant="body1">
                Two-factor authentication secures your account by requiring a code from an authenticator app (e.g. 1Password, Google Authenticator) in addition to your login credentials.
              </Typography>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBeginSetup}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  Set up 2FA
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisableDialogOpen(true)}
                >
                  Disable 2FA
                </Button>
              </Stack>
            </Stack>
          )}

          {step === "setup" && setupData && (
            <Stack spacing={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                1. Scan QR Code or enter key
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scan this QR code with your authenticator app, or enter the secret key manually.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, alignItems: "center" }}>
                {qrSvg && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      display: "inline-block",
                    }}
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Secret Key (Base32)
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, fontFamily: "monospace", letterSpacing: 1, my: 0.5 }}>
                    {setupData.secret}
                  </Paper>
                </Box>
              </Box>

              <Divider />

              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                2. Verify Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the 6-digit code generated by your authenticator app to confirm setup.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, alignItems: "center", maxWidth: 360 }}>
                <TextField
                  label="6-Digit Code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder="123456"
                  slotProps={{ htmlInput: { maxLength: 6 } }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleConfirmSetup}
                  disabled={loading || !verifyCode.trim()}
                  sx={{ whiteSpace: "nowrap", height: 56 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & enable"}
                </Button>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Button variant="text" color="inherit" onClick={() => setStep("idle")}>
                  Cancel
                </Button>
              </Box>
            </Stack>
          )}

          {step === "recovery" && (
            <Stack spacing={3}>
              <Alert severity="warning">
                <strong>Save your recovery codes!</strong> Store these recovery codes in a safe place. If you lose access to your authenticator device, these codes are the ONLY way to regain access to your account.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Recovery Codes (Shown once)
                  </Typography>
                  <Tooltip title={copied ? "Copied!" : "Copy all"}>
                    <IconButton size="small" onClick={handleCopyRecoveryCodes}>
                      {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box component="pre" sx={{ fontFamily: "monospace", m: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>
                  {recoveryCodes.join("\n")}
                </Box>
              </Paper>

              <Box>
                <Button variant="contained" color="primary" onClick={handleFinishSetup}>
                  Done
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable 2FA</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your current 6-digit TOTP code to confirm disabling two-factor authentication.
          </Typography>
          {disableError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {disableError}
            </Alert>
          )}
          <TextField
            label="Current TOTP Code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="123456"
            fullWidth
            slotProps={{ htmlInput: { maxLength: 6 } }}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisable2FA}
            color="error"
            variant="contained"
            disabled={disableLoading}
          >
            {disableLoading ? <CircularProgress size={20} color="inherit" /> : "Disable 2FA"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
